import { useState } from 'react'
import styles from './StarRating.module.css'
import { Icon, type IconName } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { FormGroupContainer } from '@/components/FormContainer'

interface StarRatingProps {
  value?: number
  max?: number
  iconName?: IconName
  iconSize?: number
  onChange?: (value: number) => unknown
  testId?: string
}

export function StarRating({
  value = 0,
  onChange,
  max = 5,
  iconName = 'star',
  iconSize,
  testId,
}: StarRatingProps) {
  const [valueBeingHoveredOn, setValueBeingHoveredOn] = useState(0)
  const createChangeHandler = (newValue: number) => () => {
    if (!onChange) {
      return
    }

    if (newValue !== value) {
      onChange(newValue)
    }
  }

  return (
    <FormGroupContainer className={styles.formContainer}>
      <div className={styles.root}>
        {Array.from({ length: max }).map((_, index) => {
          const starValue = index + 1
          return (
            <Star
              key={starValue}
              starValue={starValue}
              actualValue={value}
              iconName={iconName}
              iconSize={iconSize}
              testId={testId && `${testId}-${starValue}`}
              valueBeingHoveredOn={valueBeingHoveredOn}
              onPress={createChangeHandler(starValue)}
              onHoverChange={(hovering) =>
                setValueBeingHoveredOn(hovering ? starValue : 0)
              }
            ></Star>
          )
        })}
      </div>
    </FormGroupContainer>
  )
}

interface StarProps {
  starValue: number
  actualValue: number
  valueBeingHoveredOn: number
  iconName: IconName
  iconSize?: number
  onPress(): unknown
  onHoverChange(hovering: boolean): unknown
  testId?: string
}

function Star({
  starValue,
  actualValue,
  valueBeingHoveredOn,
  onPress,
  onHoverChange,
  iconName,
  iconSize,
  testId,
}: StarProps) {
  const valueToCompareWith =
    valueBeingHoveredOn === 0 ? actualValue : valueBeingHoveredOn
  const filled = valueToCompareWith >= starValue
  const half =
    starValue === Math.ceil(valueToCompareWith) &&
    Math.ceil(valueToCompareWith) > valueToCompareWith

  return (
    <RawButton
      className={styles.star}
      onPress={onPress}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      style={{ fontSize: iconSize }}
      testId={testId}
    >
      <span className={styles.under}>
        <Icon auto name={iconName} />
      </span>
      <span
        className={styles.over}
        style={{ width: half ? '50%' : filled ? '100%' : '0%' }}
      >
        <Icon auto name={iconName} />
      </span>
    </RawButton>
  )
}
